import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 180000, // 3 minutes for PDF exports with many images
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response, config } = error;
    
    // Check if this request should skip automatic error handling
    const skipAutoToast = config?.skipAutoToast || 
                         config?.responseType === 'blob' || // Skip for file downloads
                         config?.url?.includes('/exports/'); // Skip for export endpoints
    
    if (skipAutoToast) {
      return Promise.reject(error);
    }
    
    // Handle different error status codes
    if (response) {
      switch (response.status) {
        case 401:
          // Unauthorized - redirect to login
          if (window.location.pathname !== '/login') {
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            window.location.href = '/login';
          }
          break;
        case 403:
          // Forbidden
          toast.error('You do not have permission to perform this action');
          break;
        case 422:
          // Validation error
          const validationErrors = response.data?.errors;
          if (validationErrors && Array.isArray(validationErrors)) {
            validationErrors.forEach(err => toast.error(err.message || err));
          } else {
            toast.error(response.data?.error || 'Validation error');
          }
          break;
        case 429:
          // Rate limit exceeded
          toast.error('Too many requests. Please try again later.');
          break;
        case 500:
          // Server error
          toast.error('Server error. Please try again later.');
          break;
        default:
          // For other errors, let the calling function handle the toast
          // Don't show automatic toasts to avoid duplicates
          break;
      }
    }
    
    return Promise.reject(error);
  }
);

// API utility functions
export const apiUtils = {
  // CMS helpers
  cms: {
    // Pages
    getPage(pageName) {
      return apiUtils.fetchOne(`/pages/${pageName}`);
    },
    updatePage(pageName, payload) {
      return apiUtils.update(`/pages/${pageName}`, payload);
    },
    reorderPage(pageName, order) {
      return apiUtils.update(`/pages/${pageName}/reorder`, { order });
    },

    // Global Sections
    listGlobalSections(params = {}) {
      return apiUtils.fetchWithPagination('/global-sections', params);
    },
    getGlobalSection(sectionKey) {
      return apiUtils.fetchOne(`/global-sections/${sectionKey}`);
    },
    createGlobalSection(payload) {
      return apiUtils.create('/global-sections', payload);
    },
    updateGlobalSection(sectionKey, payload) {
      return apiUtils.update(`/global-sections/${sectionKey}`, payload);
    },
    deleteGlobalSection(sectionKey, force = false) {
      return apiUtils.delete(`/global-sections/${sectionKey}${force ? '?force=true' : ''}`);
    },

    // Metadata
    listSectionTypes() {
      return apiUtils.fetchOne('/section-types');
    },
  },
  // Generic GET request with pagination
  async fetchWithPagination(endpoint, params = {}) {
    try {
      const response = await api.get(endpoint, { 
        params,
        skipAutoToast: true 
      });
      return {
        data: response.data.data || [],
        pagination: response.data.pagination || {},
        total: response.data.count || 0,
        success: true,
      };
    } catch (error) {
      return {
        data: [],
        pagination: {},
        total: 0,
        success: false,
        error: error.response?.data?.error || 'Failed to fetch data',
      };
    }
  },

  // Generic POST request
  async create(endpoint, data, config = {}) {
    try {
      const response = await api.post(endpoint, data, { 
        ...config, 
        skipAutoToast: true 
      });
      return {
        data: response.data.data || response.data,
        success: true,
      };
    } catch (error) {
      return {
        data: null,
        success: false,
        error: error.response?.data?.error || 'Failed to create resource',
      };
    }
  },

  // Generic PUT request
  async update(endpoint, data, config = {}) {
    try {
      const response = await api.put(endpoint, data, { 
        ...config, 
        skipAutoToast: true 
      });
      return {
        data: response.data.data || response.data,
        success: true,
      };
    } catch (error) {
      return {
        data: null,
        success: false,
        error: error.response?.data?.error || 'Failed to update resource',
      };
    }
  },

  // Generic DELETE request
  async delete(endpoint) {
    try {
      const response = await api.delete(endpoint, { skipAutoToast: true });
      return {
        success: true,
        message: response.data.message || 'Resource deleted successfully',
        data: response.data?.data,
        details: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete resource',
        details: error.response?.data,
      };
    }
  },

  // Generic GET request for single resource
  async fetchOne(endpoint) {
    try {
      const response = await api.get(endpoint, { skipAutoToast: true });
      return {
        data: response.data.data || response.data,
        success: true,
      };
    } catch (error) {
      return {
        data: null,
        success: false,
        error: error.response?.data?.error || 'Failed to fetch resource',
      };
    }
  },

  // File upload helper
  async uploadFile(endpoint, formData, onProgress = null) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      };

      const response = await api.post(endpoint, formData, { 
        ...config, 
        skipAutoToast: true 
      });
      return {
        data: response.data.data || response.data,
        success: true,
      };
    } catch (error) {
      return {
        data: null,
        success: false,
        error: error.response?.data?.error || 'Failed to upload file',
      };
    }
  },

  // Download file helper
  async downloadFile(endpoint, filename = 'download') {
    try {
      // Extract query parameters from the endpoint URL
      const [baseEndpoint, queryString] = endpoint.split('?');
      const params = {};
      
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        for (const [key, value] of urlParams.entries()) {
          params[key] = value;
        }
      }
      
      const response = await api.get(baseEndpoint, {
        responseType: 'blob',
        params: params,
        skipAutoToast: true, // Skip automatic toast messages
      });

      // Check if the response is actually an error disguised as a blob
      if (response.data.type === 'application/json') {
        try {
          const text = await response.data.text();
          const errorData = JSON.parse(text);
          return {
            success: false,
            error: errorData.error || errorData.message || 'Download failed',
          };
        } catch (parseError) {
          return {
            success: false,
            error: 'Invalid response from server',
          };
        }
      }

      // Create blob link to download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      // Handle different types of errors with specific messages
      if (error.response) {
        // Server responded with error
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            return {
              success: false,
              error: errorData.error || errorData.message || 'Download failed',
            };
          } catch (parseError) {
            // If blob can't be parsed, provide status-based error
            switch (error.response.status) {
              case 404:
                return { success: false, error: 'No data found to export' };
              case 403:
                return { success: false, error: 'You do not have permission to export this data' };
              case 500:
                return { success: false, error: 'Server error occurred during export' };
              default:
                return { success: false, error: `Export failed with status ${error.response.status}` };
            }
          }
        } else {
          // Regular error response
          return {
            success: false,
            error: error.response.data?.error || error.response.data?.message || `Export failed with status ${error.response.status}`,
          };
        }
      } else if (error.code === 'ECONNABORTED') {
        // Timeout error
        return {
          success: false,
          error: 'Export request timed out. Please try again.',
        };
      } else if (error.message === 'Network Error') {
        // Network error
        return {
          success: false,
          error: 'Network error. Please check your connection.',
        };
      } else {
        // Other errors
        return {
          success: false,
          error: error.message || 'Failed to download file',
        };
      }
    }
  },
};

export default api;